import { ClientDataTable, ClientDataTableProps } from './data-table-client';
import { ServerDataTable, ServerDataTableProps } from './data-table-server';
import { DataTableVisual } from './data-table-visual';
import { DataTableProps as VisualDataTableProps } from './data-table.types';

/**
 * Unified DataTable component with multiple operation modes
 *
 * ## Variants:
 *
 * ### `variant="client"` - Client-Side Operations (Recommended for most cases)
 * - Built-in TanStack Table for sorting, filtering, pagination
 * - All operations happen in the browser
 * - Quick and easy to use
 *
 * @example
 * ```tsx
 * <DataTable
 *   variant="client"
 *   columns={columns}
 *   data={myData}
 *   enableSearch
 *   enablePagination
 *   pageSize={10}
 * />
 * ```
 *
 * ### `variant="server"` - Server-Side Operations
 * - Manual pagination and sorting
 * - Parent fetches data from server
 * - Good for large datasets
 *
 * @example
 * ```tsx
 * <DataTable
 *   variant="server"
 *   columns={columns}
 *   data={serverData}
 *   page={page}
 *   pageCount={totalPages}
 *   onPageChange={setPage}
 *   sorting={sorting}
 *   onSortingChange={setSorting}
 *   loading={isLoading}
 * />
 * ```
 *
 * ### `variant="visual"` - Pure Visual (Advanced)
 * - Maximum flexibility
 * - Parent controls everything
 * - For custom implementations
 *
 * @example
 * ```tsx
 * <DataTable
 *   variant="visual"
 *   columns={visualColumns}
 *   rows={processedData}
 *   sortedColumn={sortCol}
 *   sortDirection={sortDir}
 *   onSort={handleSort}
 * />
 * ```
 */

// Type for client variant
type ClientVariantProps<TData> = {
  variant: 'client';
} & Omit<ClientDataTableProps<TData>, 'variant'>;

// Type for server variant
type ServerVariantProps<TData> = {
  variant: 'server';
} & Omit<ServerDataTableProps<TData>, 'variant'>;

// Type for visual variant
type VisualVariantProps<TData> = {
  variant: 'visual';
} & Omit<VisualDataTableProps<TData>, 'variant'>;

// Discriminated union of all variants
export type DataTableAllProps<TData = unknown> =
  | ClientVariantProps<TData>
  | ServerVariantProps<TData>
  | VisualVariantProps<TData>;

/**
 * DataTable - Unified table component with multiple operation modes
 *
 * Choose the variant based on your needs:
 * - `client`: Easy, built-in TanStack Table (most common)
 * - `server`: Server-side pagination/sorting
 * - `visual`: Maximum flexibility (advanced)
 */
export function DataTable<TData = unknown>(props: DataTableAllProps<TData>) {
  const { variant, ...restProps } = props;

  switch (variant) {
    case 'client':
      return <ClientDataTable {...(restProps as Omit<ClientVariantProps<TData>, 'variant'>)} />;
    case 'server':
      return <ServerDataTable {...(restProps as Omit<ServerVariantProps<TData>, 'variant'>)} />;
    case 'visual':
      return <DataTableVisual {...(restProps as Omit<VisualVariantProps<TData>, 'variant'>)} />;
    default: {
      // TypeScript should never reach here due to discriminated union
      const exhaustiveCheck: never = variant;
      throw new Error(`Unknown variant: ${exhaustiveCheck}`);
    }
  }
}

// Re-export types for convenience
export type { ClientDataTableProps, ServerDataTableProps };
export type { DataTableProps as VisualDataTableProps } from './data-table.types';
